// web/src/api/notes.ts
//
// Typed wrappers for /api/v1/notes/*. Uses the shared apiFetch from
// client.ts so JWT + X-Organization-Id injection + 401 retry are uniform.
//
// After the news-analyzer-api repo's openapi.json is regenerated with the
// notes routes, run `npm run gen:api-types` and consider replacing the
// inline types here with imports from `./generated`. Keep the wrapper
// shape stable so callers don't churn.

import { apiFetch } from "./client";

export type NoteTargetKind = "cluster" | "analysis" | "material";

export interface NoteOut {
  id: string;
  user_id: string;
  target_kind: NoteTargetKind | null;
  target_id: string | null;
  body: string;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface NoteCreate {
  target_kind?: NoteTargetKind | null;
  target_id?: string | null;
  body: string;
}

export interface NotePatch {
  body?: string;
  archived?: boolean;
}

export interface NotesListChronological {
  items: NoteOut[];
  next_cursor: string | null;
}

export interface NotesGroup {
  target:
    | { kind: "freeform" }
    | { kind: "cluster"; id: string; title: string | null }
    | { kind: "analysis"; id: string; material_id: string | null }
    | { kind: "material"; id: string; display_name: string | null };
  notes: NoteOut[];
}

export interface NotesListGrouped {
  groups: NotesGroup[];
  next_cursor: string | null;
}

export interface NotesListParams {
  target_kind?: NoteTargetKind | "freeform" | null;
  material_id?: string | null;
  archived?: boolean;
  group_by?: "chronological" | "attachment";
  cursor?: string | null;
  limit?: number;
}

export interface NotesByTargetResponse {
  items: NoteOut[];
}

export interface NotesRecentResponse {
  items: NoteOut[];
}

export type NotesCountsResponse = Record<string, number>;

function buildQuery(params: Record<string, string | number | boolean | null | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

export const notesApi = {
  list: (params: NotesListParams) => {
    const groupBy = params.group_by ?? "attachment";
    const qs = buildQuery({
      group_by: groupBy,
      target_kind: params.target_kind ?? undefined,
      material_id: params.material_id ?? undefined,
      archived: params.archived ?? false,
      cursor: params.cursor ?? undefined,
      limit: params.limit ?? 50,
    });
    return apiFetch<NotesListChronological | NotesListGrouped>(`/api/v1/notes${qs}`);
  },

  byTarget: ({ targetKind, targetId }: { targetKind: NoteTargetKind; targetId: string }) =>
    apiFetch<NotesByTargetResponse>(
      `/api/v1/notes/by-target?target_kind=${targetKind}&target_id=${encodeURIComponent(targetId)}`
    ),

  recent: ({ limit }: { limit: number }) =>
    apiFetch<NotesRecentResponse>(`/api/v1/notes/recent?limit=${limit}`),

  counts: async (
    targets: Array<{ kind: NoteTargetKind; id: string }>
  ): Promise<NotesCountsResponse> => {
    if (targets.length === 0) return {};
    const serialised = targets.map((t) => `${t.kind}:${t.id}`).join(",");
    return apiFetch<NotesCountsResponse>(`/api/v1/notes/counts?targets=${serialised}`);
  },

  create: (body: NoteCreate) =>
    apiFetch<NoteOut>("/api/v1/notes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  patch: (id: string, body: NotePatch) =>
    apiFetch<NoteOut>(`/api/v1/notes/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  remove: (id: string) =>
    apiFetch<void>(`/api/v1/notes/${id}`, { method: "DELETE" }),
};
